import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { FileSearch, ChevronRight } from 'lucide-react';

/** Small horizontal SOP chip strip driven by project stage.
 *  Reads /api/search/sops/for-stage?stage=... and renders "primary" (stage-relevant)
 *  chips first, then a subtle "General" group for cross-cutting SOPs.
 *  Chip click → navigates to /knowledge/{sop_id}. */
export function StageSOPChips({ stage, compact = false, testId = 'sop-chips' }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!stage) { setData(null); return; }
    let alive = true;
    setLoading(true);
    api.get('/search/sops/for-stage', { params: { stage, limit: compact ? 4 : 6 } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [stage, compact]);

  if (!stage) return null;
  if (loading && !data) {
    return <div className="text-[11px] text-[hsl(var(--foreground-subtle))]" data-testid={`${testId}-loading`}>Loading SOPs…</div>;
  }
  if (!data || (data.primary.length === 0 && data.cross_cutting.length === 0)) return null;

  const goSop = (id) => nav(`/knowledge/${id}`);

  return (
    <div className="flex items-start gap-2 flex-wrap" data-testid={testId}>
      <div className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--foreground-muted))] font-semibold pt-1">
        <FileSearch size={11} className="text-[hsl(var(--primary))]" />
        Related SOPs
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {data.primary.map(s => (
          <button
            key={s.id}
            onClick={() => goSop(s.id)}
            data-testid={`${testId}-${s.sop_code}`}
            title={s.title}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] transition-all
                       border-[hsl(var(--primary)/.35)] bg-[hsl(var(--primary)/.06)] text-[hsl(var(--foreground))]
                       hover:bg-[hsl(var(--primary)/.14)] hover:border-[hsl(var(--primary))]"
          >
            <span className="font-mono opacity-80">{(s.sop_code || '').replace('SOP ', '')}</span>
            <span className="max-w-[180px] truncate">{titleOnly(s)}</span>
            <ChevronRight size={10} className="opacity-60" />
          </button>
        ))}
        {!compact && data.cross_cutting.length > 0 && (
          <>
            <span className="mx-0.5 h-3 w-px bg-[hsl(var(--border))]" aria-hidden />
            {data.cross_cutting.map(s => (
              <button
                key={s.id}
                onClick={() => goSop(s.id)}
                data-testid={`${testId}-${s.sop_code}`}
                title={`${s.title} · general`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] transition-all
                           border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))]
                           hover:bg-[hsl(var(--surface-2))]"
              >
                <span className="font-mono opacity-80">{(s.sop_code || '').replace('SOP ', '')}</span>
                <span className="max-w-[140px] truncate">{titleOnly(s)}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function titleOnly(sop) {
  // Titles are like "SOP B1 — Concept Development". Strip the "SOP B1 — " prefix.
  const t = sop.title || '';
  const idx = t.indexOf('—');
  return idx > 0 ? t.slice(idx + 1).trim() : t;
}
